// Vendor shell left rail — logo, Primary nav group, My Account group, bottom
// Logout. See Spec 3 (docs/connect/2026-07-09-connect-2.0-app-shell-navigation.md),
// Task 2.
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ShellNavService } from '../shell-nav.service';
import { AuthenticationService } from '../../services/authentication.service';
import { ThemeService } from '../../services/theme.service';
import { AliasSessionService } from '../../services/alias-session.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  protected readonly nav = inject(ShellNavService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  protected readonly theme = inject(ThemeService);
  protected readonly aliasSession = inject(AliasSessionService);

  /**
   * Origin-aware "Active" highlight. The per-site workspace lives at
   * `/active/:id` and is reachable from BOTH the Active list and the (nav-less,
   * email-only) Project Overview page. So `routerLinkActive` can't be used for
   * this link: it would highlight Active on every `/active/:id` visit, even the
   * ones that came from Project Overview — where no nav item should light up.
   *
   * Rule: highlight on the Active list (`/active`), and on the workspace
   * (`/active/:id`) UNLESS it was entered from Project Overview (the routerLink
   * that opened it stamped `workspaceBackType: 'project-overview'` onto
   * `history.state`). A direct/email link to `/active/:id` has no such state, so
   * it highlights Active — the sensible default for the active-work view.
   *
   * A getter (not a subscription): Angular re-evaluates it each change-detection
   * pass, which includes every navigation, so the class stays in sync.
   */
  get activeNavActive(): boolean {
    const url = this.router.url.split(/[?#]/)[0];
    if (url === '/active') {
      return true;
    }
    if (url.startsWith('/active/')) {
      return window.history.state?.['workspaceBackType'] !== 'project-overview';
    }
    return false;
  }

  /** Close the drawer after a nav selection (only matters in drawer mode). */
  onNavSelect(): void {
    this.nav.close();
  }

  /**
   * Reuses the existing shared logout flow (AuthenticationService.logout():
   * clear storage + navigate `/login` + full reload) — the same logic
   * `header.component.ts` already delegates to, so this doesn't fork a second
   * copy of the clear-storage/navigate/reload sequence duplicated inline in
   * `account-side-nav.component.ts`.
   */
  logout(): void {
    this.nav.close();
    this.authenticationService.logout();
  }

  /**
   * Flip light/dark and persist the choice to the user.
   *
   * Disabled during a read-only alias session (the button is `[disabled]` in
   * the template): the theme flag persists via `PUT /user/dark-mode`, which the
   * server's read-only filter blocks with a 403, and flipping the theme locally
   * anyway would both fire the "not available in read-only guidance mode"
   * warning AND appear to succeed — a contradictory signal. Per Product, the
   * toggle is simply unavailable while viewing a vendor's portal. The guard here
   * is belt-and-suspenders behind the disabled attribute.
   */
  toggleTheme(): void {
    if (this.aliasSession.isAliased()) {
      return;
    }
    this.theme.toggle();
  }
}
